import { FieldValues, Path, PathValue, useController, useFormContext } from 'react-hook-form';
import { RadioGroup as ChakraRadioGroup, RadioGroupItem, RadioGroupItemText, RadioGroupRoot } from '@chakra-ui/react';
import { Field, FieldProps, RadioGroupItemHiddenInput, RadioGroupItemIndicator } from '../chakra-components';
import React from 'react';
import { useWatchCallback } from './useWatchCallback';

export interface RadioGroupProps<T extends FieldValues, TPath extends Path<T>> extends Omit<ChakraRadioGroup.RootProps, 'value' | 'onChange'> {
    ref?: React.Ref<HTMLDivElement>;
    linkTo: TPath;
    disabled?: boolean;
    label?: React.ReactNode;
    options: { value: PathValue<T, TPath>, label: React.ReactNode }[];
    fieldProps?: FieldProps;
    onWatch?: (value: PathValue<T, TPath>) => void;
}

export type RadioGroupRef = HTMLDivElement;

export const RadioGroup = <T extends FieldValues, TPath extends Path<T>>(props: RadioGroupProps<T, TPath> & { ref?: React.Ref<RadioGroupRef> }) => {
	const { ref, fieldProps, disabled, label, linkTo, options, onWatch, ...rootProps } = props;

	const { control } = useFormContext<T>();
	const { fieldState, field } = useController<T>({ name: linkTo, control, disabled });
    
	useWatchCallback(control, linkTo, onWatch);
    
	const invalid = fieldState.error?.message !== undefined;

	return (
		<Field errorText={fieldState.error?.message} invalid={invalid} {...fieldProps}>
			<RadioGroupRoot ref={ref} value={field.value} onValueChange={({value}) => field.onChange(value)} {...rootProps}>
				{options.map((option, index) => (
					<RadioGroupItem key={index} value={option.value}>
						<RadioGroupItemHiddenInput />
						<RadioGroupItemIndicator />
						<RadioGroupItemText>{option.label}</RadioGroupItemText>
					</RadioGroupItem>
				))}
			</RadioGroupRoot>
		</Field>
	);
};